import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Database, CheckCircle2, AlertTriangle, Loader2, FileArchive, ShieldAlert, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AdminMigracion() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/migration-status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const ejecutarMigracion = async () => {
    if (confirmText.trim().toUpperCase() !== "IMPORTAR") {
      toast.error("Escribe IMPORTAR (en mayúsculas) para confirmar.");
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/import-snapshot", null, {
        params: { force: status?.already_imported || false, clear_target: true },
      });
      setResult(data);
      toast.success(`Migración completada · ${data?.summary?.total_inserted?.toLocaleString() || 0} documentos importados`);
      await loadStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
      setConfirmText("");
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-migracion-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#6f8d7f] font-semibold">ADMINISTRACIÓN</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Migración de datos
        </h1>
        <p className="text-sm text-[#5c7368] mt-1">
          Carga el snapshot de datos desde el ambiente de desarrollo a esta base de datos.
          Esta acción es irreversible — se recomienda ejecutar UNA SOLA VEZ tras el primer despliegue.
        </p>
      </header>

      {loading && (
        <Card className="p-6 flex items-center gap-3 text-[#5c7368]">
          <Loader2 className="animate-spin" size={20} /> Verificando estado…
        </Card>
      )}

      {status && (
        <Card className="p-6 border border-[#d1dbd6]" data-testid="migration-status-card">
          <h2 className="text-lg font-semibold text-[#15241d] mb-4 flex items-center gap-2">
            <FileArchive size={18} className="text-[#205c45]" />
            Estado del snapshot
          </h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt className="text-xs uppercase tracking-wider text-[#5c7368]">Snapshot disponible</dt>
              <dd className="mt-1 flex items-center gap-2">
                {status.snapshot_present ? (
                  <Badge className="bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]"><CheckCircle2 size={12} className="mr-1" /> Sí</Badge>
                ) : (
                  <Badge className="bg-[#fdecea] text-[#b53a25] border-[#d99584]"><AlertTriangle size={12} className="mr-1" /> No encontrado</Badge>
                )}
                {status.snapshot_size_mb && (
                  <span className="text-sm text-[#15241d] tabular-nums">{status.snapshot_size_mb} MB</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-[#5c7368]">Ya importado</dt>
              <dd className="mt-1">
                {status.already_imported ? (
                  <Badge className="bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]">
                    <CheckCircle2 size={12} className="mr-1" /> Sí, el {(status.completed_at || "").slice(0, 16).replace("T", " ")}
                  </Badge>
                ) : (
                  <Badge className="bg-[#fef7ec] text-[#8a5500] border-[#f5c477]">Pendiente</Badge>
                )}
              </dd>
            </div>
            {status.imported_summary && (
              <div className="md:col-span-2">
                <dt className="text-xs uppercase tracking-wider text-[#5c7368]">Resumen de la última importación</dt>
                <dd className="mt-1 text-sm text-[#15241d]">
                  {status.imported_summary.total_inserted?.toLocaleString()} documentos · {status.imported_summary.collections_count} colecciones
                  {status.imported_summary.errors_count > 0 && (
                    <span className="text-[#b53a25] ml-2">· {status.imported_summary.errors_count} errores</span>
                  )}
                </dd>
              </div>
            )}
          </dl>
        </Card>
      )}

      {status?.snapshot_present && (
        <Card className="p-6 border-2 border-[#f5c477] bg-gradient-to-br from-[#fef7ec] to-white">
          <h2 className="text-lg font-bold text-[#15241d] mb-2 flex items-center gap-2">
            <ShieldAlert size={20} className="text-[#8a5500]" />
            {status.already_imported ? "Re-ejecutar migración" : "Ejecutar migración"}
          </h2>
          <ul className="text-sm text-[#4a5c53] space-y-1.5 mb-4 list-disc pl-5">
            <li>Se vaciarán las colecciones destino antes de insertar (clientes, proveedores, facturas, lotes, etc.).</li>
            <li>Se importarán <b>todos los datos</b> del snapshot incluido el usuario admin renombrado a <code className="bg-white px-1.5 py-0.5 rounded text-xs">gerencia@pharma-stern.com</code>.</li>
            <li>El PIN de gerencia (Mario, 9988) y todas las contraseñas se preservan.</li>
            <li>Tras importar, vuelve a iniciar sesión con <code className="bg-white px-1.5 py-0.5 rounded text-xs">gerencia@pharma-stern.com</code> + tu password admin.</li>
          </ul>

          <div className="bg-white border border-[#d1dbd6] rounded-md p-4 mb-4">
            <label className="text-xs uppercase tracking-wider text-[#5c7368] font-semibold block mb-2">
              Para confirmar, escribe la palabra <code className="bg-[#fef7ec] px-1.5 py-0.5 rounded">IMPORTAR</code>
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="IMPORTAR"
              className="w-full max-w-xs px-3 py-2 border border-[#d1dbd6] rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#8a5500]"
              data-testid="confirm-text-input"
              disabled={importing}
            />
          </div>

          <Button
            onClick={ejecutarMigracion}
            disabled={importing || confirmText.trim().toUpperCase() !== "IMPORTAR"}
            className="bg-[#8a5500] hover:bg-[#6d4400] text-white"
            data-testid="run-migration-btn"
          >
            {importing ? (
              <><Loader2 className="animate-spin mr-2" size={16} /> Importando…</>
            ) : (
              <><Database className="mr-2" size={16} /> {status.already_imported ? "Re-importar" : "Ejecutar migración"}</>
            )}
          </Button>
        </Card>
      )}

      {result && (
        <Card className="p-6 border border-[#b2d0c2] bg-[#e9f2ee]" data-testid="migration-result-card">
          <h2 className="text-lg font-bold text-[#15241d] flex items-center gap-2 mb-3">
            <CheckCircle2 size={20} className="text-[#205c45]" /> Migración completada
          </h2>
          <div className="text-sm text-[#15241d] space-y-1">
            <div>Inicio: <span className="tabular-nums">{(result.started_at || "").slice(0, 19).replace("T", " ")}</span></div>
            <div>Fin: <span className="tabular-nums">{(result.completed_at || "").slice(0, 19).replace("T", " ")}</span></div>
            <div className="font-semibold pt-2">
              Total importado: {result.summary?.total_inserted?.toLocaleString()} documentos
            </div>
            {result.summary?.errors?.length > 0 && (
              <div className="mt-3 p-3 bg-[#fdecea] border border-[#d99584] rounded">
                <div className="font-semibold text-[#b53a25] mb-1 text-sm">⚠️ {result.summary.errors.length} errores:</div>
                <ul className="text-xs space-y-0.5">
                  {result.summary.errors.slice(0, 5).map((e, i) => (
                    <li key={i}><b>{e.collection}</b>: {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <details className="mt-4 text-xs">
            <summary className="cursor-pointer text-[#5c7368] font-semibold uppercase tracking-wider">Detalle por colección</summary>
            <div className="mt-2 max-h-64 overflow-y-auto bg-white rounded border p-3">
              {Object.entries(result.summary?.per_collection || {}).map(([col, info]) => (
                <div key={col} className="flex justify-between py-0.5 border-b last:border-b-0">
                  <span className="font-mono text-[11px]">{col}</span>
                  <span className="tabular-nums text-[#205c45]">{info.inserted?.toLocaleString() || 0}</span>
                </div>
              ))}
            </div>
          </details>
        </Card>
      )}

      {/* ============ ITER83 — Sincronización catálogo (precios + IVA + plazos) ============ */}
      <SyncCatalogo83Card />
      {/* ============ Reabrir CxC marcadas pagadas sin cobro real ============ */}
      <ReabrirCxcCard />
      {/* ============ ITER96 — Reconciliar facturas vendidas POR CAJA descontadas como UNIDAD ============ */}
      <ReconciliarCajasCard />
      {/* ============ ITER60 — Limpieza de datos test ============ */}
      <CleanupCard />
      {/* ============ Sync COBROS-PAGOS 2026 v2 ============ */}
      <Sync2026V2Card />
      {/* ============ Reconciliación CARTERA 2026 ============ */}
      <CarteraReconcileCard />
      {/* ============ Inventario 2026 ============ */}
      <InventarioUpdateCard />
      {/* ============ Recalcular nombres comerciales ============ */}
      <RecalcularNombresCard />
      {/* ============ Feb 2026 — Sincronizar precio_caja_venta con precio_venta × upc ============ */}
      <SyncPrecioCajaCard />
      {/* ============ Feb 2026 — Restaurar costos en 0 desde histórico ============ */}
      <RestaurarCostosCard />
      {/* ============ Diagnóstico BD ============ */}
      <DiagnosticoCard />
    </div>
  );
}



function SyncCatalogo83Card() {
  const [loading, setLoading] = useState(null); // 'precios' | 'iva' | 'plazos' | 'all' | null
  const [results, setResults] = useState({});

  const run = async (key, endpoint, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    setLoading(key);
    try {
      const { data } = await api.post(endpoint);
      setResults((r) => ({ ...r, [key]: data }));
      toast.success("Migración completada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(null);
    }
  };

  const runAll = async () => {
    if (!window.confirm(
      "🔄 Aplicar las 3 sincronizaciones en orden:\n\n" +
      "  1. Sincronizar precio_venta y costo de lotes desde producto comercial\n" +
      "  2. Migrar IVA exento → IVA 0%\n" +
      "  3. Recalcular dias_credito en facturas históricas\n" +
      "  4. Backfill subtotal/IVA en cotizaciones y notas de pedido\n\n" +
      "Las 4 son idempotentes (puedes repetirlas sin problema). ¿Continuar?"
    )) return;
    setLoading("all");
    try {
      const r1 = await api.post("/masters/lotes/sincronizar-precios-desde-producto");
      const r2 = await api.post("/masters/productos-comerciales/migrar-iva-exento-a-cero");
      const r3 = await api.post("/invoicing/facturas/recalcular-dias-credito");
      const r4 = await api.post("/admin/backfill-iva-cotizaciones-pedidos");
      setResults({ precios: r1.data, iva: r2.data, plazos: r3.data, iva_cotpedidos: r4.data });
      toast.success("4 migraciones completadas correctamente");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(null);
    }
  };

  return (
    <Card className="p-6 mt-6 border-2 border-[#205c45]/40 bg-gradient-to-br from-[#e9f2ee] to-white" data-testid="sync-catalogo-83-card">
      <h2 className="text-xl font-bold text-[#15241d] mb-2 flex items-center gap-2">
        <Database size={20} className="text-[#205c45]" />
        Sincronizar catálogo (precios + IVA + plazos) · ITER83
      </h2>
      <p className="text-sm text-[#4a5c53] mb-4">
        Aplica 3 correcciones de datos críticas en este entorno. Cada acción es <strong>idempotente</strong>:
        solo modifica registros que aún no están sincronizados.
      </p>

      <div className="space-y-3">
        <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm text-[#15241d]">1️⃣ Sincronizar precio y costo de lotes</h3>
            <p className="text-xs text-[#5c7368] mt-1">
              Para cada lote con <code>precio_venta = 0</code> o <code>costo_unitario = 0</code>, hereda el valor
              actual del producto comercial padre. Corrige el bug estilo <strong>TOBRACORT</strong>.
            </p>
            {results.precios && (
              <div className="text-[11px] text-[#205c45] mt-2">
                ✓ {results.precios.lotes_actualizados} de {results.precios.lotes_revisados} lotes actualizados
              </div>
            )}
          </div>
          <Button
            onClick={() => run("precios", "/masters/lotes/sincronizar-precios-desde-producto",
              "Sincronizar precio_venta y costo_unitario en lotes con valores en 0?\n\nIdempotente.")}
            disabled={!!loading}
            size="sm"
            className="bg-[#205c45] hover:bg-[#1a4a37]"
            data-testid="sync-precios-lotes-btn"
          >
            {loading === "precios" ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
            Sincronizar
          </Button>
        </div>

        <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm text-[#15241d]">2️⃣ Migrar IVA exento → IVA 0%</h3>
            <p className="text-xs text-[#5c7368] mt-1">
              Cambia los productos con <code>iva_exento=true</code> a <code>iva_porcentaje=0</code> (no exento).
              El SRI los registrará como "subtotal 0%" (cod 0) en lugar de "exento" (cod 7).
            </p>
            {results.iva && (
              <div className="text-[11px] text-[#205c45] mt-2">
                ✓ {results.iva.productos_actualizados} productos migrados a IVA 0%
              </div>
            )}
          </div>
          <Button
            onClick={() => run("iva", "/masters/productos-comerciales/migrar-iva-exento-a-cero",
              "Migrar todos los productos exentos a IVA 0%?\n\nIdempotente.")}
            disabled={!!loading}
            size="sm"
            className="bg-[#205c45] hover:bg-[#1a4a37]"
            data-testid="sync-iva-btn"
          >
            {loading === "iva" ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
            Migrar IVA
          </Button>
        </div>

        <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm text-[#15241d]">💳 Backfill vendedor en cobros existentes</h3>
            <p className="text-xs text-[#5c7368] mt-1">
              Asigna <code>vendedor_id</code> a los cobros antiguos infiriéndolo desde
              la CxC aplicada o desde el cliente. Idempotente. Necesario para que cada
              vendedor vea su cartera de cobros en Rotación Comercial.
            </p>
            {results.backfill_vendedor && (
              <div className="text-[11px] text-[#205c45] mt-2">
                ✓ {results.backfill_vendedor.cobros_actualizados} cobros actualizados · {results.backfill_vendedor.cobros_sin_vendedor_inferible} sin vendedor inferible
              </div>
            )}
          </div>
          <Button
            onClick={() => run("backfill_vendedor", "/masters/cobros/backfill-vendedor",
              "¿Asignar vendedor a los cobros existentes que no lo tienen?\n\nIdempotente. Usa la CxC aplicada o el cliente para inferir.")}
            disabled={!!loading}
            size="sm"
            className="bg-[#205c45] hover:bg-[#1a4a37]"
            data-testid="backfill-vendedor-btn"
          >
            {loading === "backfill_vendedor" ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
            Backfill
          </Button>
        </div>

        <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm text-[#15241d]">🧬 Deduplicar lotes (mismo nº lote + producto + bodega)</h3>
            <p className="text-xs text-[#5c7368] mt-1">
              Fusiona lotes duplicados sumando cantidades y calculando costo ponderado.
              Reasigna referencias en movimientos y facturas. <b>Idempotente</b>.
            </p>
            {results.dedup_lotes && (
              <div className="text-[11px] text-[#205c45] mt-2">
                ✓ {results.dedup_lotes.grupos_fusionados} grupos fusionados · {results.dedup_lotes.lotes_eliminados} duplicados eliminados
              </div>
            )}
          </div>
          <Button
            onClick={() => run("dedup_lotes", "/masters/lotes/deduplicar",
              "Fusionar todos los lotes duplicados (mismo producto + número de lote + bodega)?\n\nAcción IDEMPOTENTE y SEGURA: suma cantidades, recalcula costo ponderado, reasigna referencias en movimientos y facturas.")}
            disabled={!!loading}
            size="sm"
            className="bg-[#205c45] hover:bg-[#1a4a37]"
            data-testid="dedup-lotes-btn"
          >
            {loading === "dedup_lotes" ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
            Deduplicar
          </Button>
        </div>

        <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm text-[#15241d]">💰 Recalcular costo promedio desde lotes</h3>
            <p className="text-xs text-[#5c7368] mt-1">
              Recalcula <code>precio_costo</code> de cada producto comercial como costo promedio
              ponderado por <code>cantidad_disponible</code> de sus lotes activos. Corrige el bug
              histórico de doble conteo en facturas de compra previas a este fix.
            </p>
            {results.recalc_costo && (
              <div className="text-[11px] text-[#205c45] mt-2">
                ✓ {results.recalc_costo.productos_actualizados} de {results.recalc_costo.productos_revisados} productos corregidos · {results.recalc_costo.productos_sin_cambios} sin cambios
              </div>
            )}
          </div>
          <Button
            onClick={() => run("recalc_costo", "/masters/productos-comerciales/recalcular-costo-desde-lotes",
              "Recalcular precio_costo de todos los productos comerciales desde sus lotes activos?\n\nIdempotente. Genera entradas en histórico de precios con motivo='recalculo_desde_lotes'.")}
            disabled={!!loading}
            size="sm"
            className="bg-[#205c45] hover:bg-[#1a4a37]"
            data-testid="recalc-costo-btn"
          >
            {loading === "recalc_costo" ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
            Recalcular
          </Button>
        </div>

        <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm text-[#15241d]">3️⃣ Recalcular días de crédito en facturas históricas</h3>
            <p className="text-xs text-[#5c7368] mt-1">
              En facturas con <code>dias_credito = 0</code>, intenta:
              <br />1. <code>(fecha_vencimiento − fecha)</code>
              <br />2. Si da 0 y la forma de pago no es contado (01), toma el <code>dias_credito</code> actual del cliente.
              <br />Soluciona PDFs que muestran &quot;0 días&quot;. También reajusta <code>fecha_vencimiento</code> y la CxC asociada.
            </p>
            {results.plazos && (
              <div className="text-[11px] text-[#205c45] mt-2">
                ✓ {results.plazos.facturas_actualizadas} de {results.plazos.facturas_revisadas} facturas con plazo calculado
                {results.plazos.tomadas_del_cliente ? ` (${results.plazos.tomadas_del_cliente} desde el cliente)` : ""}
              </div>
            )}
          </div>
          <Button
            onClick={() => run("plazos", "/invoicing/facturas/recalcular-dias-credito",
              "Recalcular dias_credito en facturas con valor 0 o ausente?\n\nIdempotente.")}
            disabled={!!loading}
            size="sm"
            className="bg-[#205c45] hover:bg-[#1a4a37]"
            data-testid="sync-dias-credito-btn"
          >
            {loading === "plazos" ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
            Recalcular
          </Button>
        </div>

        <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm text-[#15241d]">4️⃣ Backfill Subtotal/IVA en cotizaciones y notas de pedido</h3>
            <p className="text-xs text-[#5c7368] mt-1">
              Recalcula y guarda <code>subtotal</code>, <code>iva_total</code> y <code>total</code> en cotizaciones
              y notas de pedido históricas que no tienen los campos separados. La lista ahora mostrará subtotal sin IVA, IVA y total.
            </p>
            {results.iva_cotpedidos && (
              <div className="text-[11px] text-[#205c45] mt-2">
                ✓ {results.iva_cotpedidos.fixed?.cotizaciones || 0} cotizaciones y {results.iva_cotpedidos.fixed?.notas_pedido || 0} notas de pedido actualizadas
              </div>
            )}
          </div>
          <Button
            onClick={() => run("iva_cotpedidos", "/admin/backfill-iva-cotizaciones-pedidos",
              "Recalcular subtotal, iva_total y total en cotizaciones y notas de pedido históricas?\n\nIdempotente.")}
            disabled={!!loading}
            size="sm"
            className="bg-[#205c45] hover:bg-[#1a4a37]"
            data-testid="sync-iva-cotpedidos-btn"
          >
            {loading === "iva_cotpedidos" ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
            Backfill
          </Button>
        </div>
        <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm text-[#15241d]">5️⃣ Sincronizar retenciones recibidas en CxC</h3>
            <p className="text-xs text-[#5c7368] mt-1">
              Recorre todas las retenciones registradas y actualiza <code>retencion_aplicada</code>,
              <code>saldo</code> y <code>estado</code> en cada CxC. Soluciona facturas tipo
              <strong> MEDICAL ALIANZA</strong> donde el saldo mostraba más por no descontar la retención.
              Idempotente.
            </p>
            {results.retenciones_cxc && (
              <div className="text-[11px] text-[#205c45] mt-2">
                ✓ {results.retenciones_cxc.cxc_actualizadas} CxC actualizadas
                {results.retenciones_cxc.cxc_con_discrepancia ? ` de ${results.retenciones_cxc.cxc_con_discrepancia} con discrepancia` : ""}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Button
              onClick={() => run("retenciones_cxc", "/accounting/cuentas-por-cobrar/sincronizar-retenciones?aplicar=false",
                "Dry-run (revisar sin aplicar) — sincronización de retenciones en CxC")}
              disabled={!!loading}
              size="sm"
              variant="outline"
              data-testid="sync-retenciones-cxc-dry-btn"
            >
              {loading === "retenciones_cxc" ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
              Dry-run
            </Button>
            <Button
              onClick={() => run("retenciones_cxc", "/accounting/cuentas-por-cobrar/sincronizar-retenciones?aplicar=true",
                "APLICAR — sincronizar retencion_aplicada y saldo en todas las CxC.\n\nIdempotente.")}
              disabled={!!loading}
              size="sm"
              className="bg-[#205c45] hover:bg-[#1a4a37]"
              data-testid="sync-retenciones-cxc-apply-btn"
            >
              {loading === "retenciones_cxc" ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
              Aplicar
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-[#d1dbd6] flex justify-end">
        <Button
          onClick={runAll}
          disabled={!!loading}
          className="bg-[#15241d] hover:bg-[#0a1310]"
          data-testid="sync-all-btn"
        >
          {loading === "all" ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle2 className="mr-2" size={16} />}
          Aplicar las 4 a la vez
        </Button>
      </div>
    </Card>
  );
}


function ReabrirCxcCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("dry-run");
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState("");
  const [clienteQuery, setClienteQuery] = useState("");

  useEffect(() => {
    api.get("/masters/clientes").then((r) => setClientes(r.data || [])).catch(() => {});
  }, []);

  const clienteMatches = clienteQuery.trim().length >= 2
    ? clientes.filter((c) => {
        const q = clienteQuery.toLowerCase();
        return (c.razon_social || "").toLowerCase().includes(q) || (c.numero_identificacion || "").includes(q);
      }).slice(0, 8)
    : [];

  const selectedCliente = clientes.find((c) => c.id === clienteId);

  const run = async (apply) => {
    if (apply && !window.confirm(
      `⚠️ Reabrir CxC marcadas como pagadas SIN cobro real asociado${selectedCliente ? ` (solo cliente: ${selectedCliente.razon_social})` : " (TODOS los clientes)"}.\n\n` +
      "Esto va a:\n" +
      "  · Buscar CxC con estado='pagada' o saldo=0\n" +
      "  · Verificar si tienen un cobro real registrado\n" +
      "  · Si NO tienen cobro: reabrirlas (estado='pendiente')\n" +
      "  · Si SÍ tienen cobro: dejarlas como están\n\n" +
      "Idempotente. ¿Continuar?"
    )) return;
    setLoading(true);
    setResult(null);
    setMode(apply ? "apply" : "dry-run");
    try {
      const params = new URLSearchParams({ apply: apply ? "true" : "false" });
      if (clienteId) params.set("cliente_id", clienteId);
      const { data } = await api.post(`/admin/reabrir-cxc-sin-cobro?${params.toString()}`);
      setResult(data);
      toast.success(apply
        ? `${data.aplicados} CxC reabiertas correctamente`
        : `Vista previa · ${data.candidatos} CxC se reabrirían`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 border border-[#d1dbd6]" data-testid="reabrir-cxc-card">
      <h2 className="text-lg font-semibold text-[#15241d] mb-1 flex items-center gap-2">
        <ShieldAlert size={18} className="text-[#b08400]" />
        Reabrir CxC marcadas como pagadas sin cobro real
      </h2>
      <p className="text-sm text-[#5c7368] mb-4">
        Útil cuando la importación inicial de cartera marcó algunas facturas como pagadas pero
        en realidad no se cobraron. <strong>Recomendado:</strong> filtra primero por cliente para
        evitar reabrir facturas históricas saldadas legítimamente antes del sistema.
        <strong> Ejecuta vista previa</strong> y revisa los ejemplos antes de aplicar.
      </p>
      <div className="mb-3">
        <label className="text-xs uppercase tracking-wider text-[#5c7368]">Filtrar por cliente (opcional)</label>
        <div className="relative">
          <input
            type="text"
            value={clienteQuery}
            onChange={(e) => { setClienteQuery(e.target.value); setClienteId(""); }}
            placeholder="Escribe nombre o RUC del cliente (mín. 2 letras)…"
            className="w-full mt-1 h-9 px-2 border border-[#d1dbd6] rounded text-sm"
            data-testid="reabrir-cxc-cliente-input"
          />
          {clienteMatches.length > 0 && !clienteId && (
            <div className="absolute z-10 left-0 right-0 top-full bg-white border border-[#d1dbd6] rounded mt-1 shadow-lg max-h-60 overflow-auto">
              {clienteMatches.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-[#e9f2ee]"
                  onClick={() => { setClienteId(c.id); setClienteQuery(c.razon_social); }}
                >
                  <span className="font-medium">{c.razon_social}</span>
                  <span className="text-xs text-[#5c7368] ml-2">· {c.numero_identificacion}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {selectedCliente && (
          <div className="mt-1 text-xs text-[#205c45] flex items-center gap-2">
            <CheckCircle2 size={12} /> Filtrado por: <strong>{selectedCliente.razon_social}</strong>
            <button onClick={() => { setClienteId(""); setClienteQuery(""); }} className="text-[#b53a25] underline">limpiar</button>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          onClick={() => run(false)}
          disabled={loading}
          variant="outline"
          data-testid="reabrir-cxc-preview-btn"
        >
          {loading && mode === "dry-run" ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
          Vista previa (no escribe)
        </Button>
        <Button
          onClick={() => run(true)}
          disabled={loading || !result || result.candidatos === 0}
          className="bg-[#b08400] hover:bg-[#8f6c00]"
          data-testid="reabrir-cxc-apply-btn"
        >
          {loading && mode === "apply" ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
          Aplicar (escribe)
        </Button>
      </div>
      {result && (
        <div className="mt-4 p-3 bg-[#f7f9f8] rounded border border-[#d1dbd6] text-sm">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={result.dry_run ? "bg-[#5c7368]" : "bg-[#205c45]"}>
              {result.dry_run ? "DRY-RUN" : "APLICADO"}
            </Badge>
            <span className="font-mono">
              candidatos = <strong>{result.candidatos}</strong>
              {!result.dry_run && <> · aplicados = <strong>{result.aplicados}</strong></>}
            </span>
          </div>
          {result.ejemplos?.length > 0 && (
            <div className="overflow-auto max-h-72 mt-2 text-xs">
              <table className="w-full">
                <thead className="bg-[#e9f2ee] text-[#15241d]">
                  <tr>
                    <th className="text-left p-1">Documento</th>
                    <th className="text-left p-1">Cliente</th>
                    <th className="text-right p-1">Total</th>
                    <th className="text-right p-1">Pagado (antes)</th>
                    <th className="text-right p-1">Saldo correcto</th>
                    <th className="text-left p-1">Estado antes</th>
                  </tr>
                </thead>
                <tbody>
                  {result.ejemplos.map((e) => (
                    <tr key={e.id} className="border-b border-[#d1dbd6]">
                      <td className="font-mono p-1">{e.numero_documento || e.id?.slice(0, 8)}</td>
                      <td className="p-1">{e.cliente_nombre}</td>
                      <td className="text-right tabular-nums p-1">${e.monto_total?.toFixed(2)}</td>
                      <td className="text-right tabular-nums text-[#b53a25] p-1">${e.monto_pagado_antes?.toFixed(2)}</td>
                      <td className="text-right tabular-nums text-[#205c45] font-semibold p-1">${e.saldo_correcto?.toFixed(2)}</td>
                      <td className="p-1 uppercase text-xs">{e.estado_antes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}



function InventarioUpdateCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("dry-run");

  const run = async (apply) => {
    if (apply && !window.confirm(
      "⚠️ Actualizar precios + lotes de inventario desde Excel 2026 v2.\n\n" +
      "Esto va a:\n" +
      "  · Actualizar precio_unidad, precio_caja, precio_techo, pvp en productos\n" +
      "  · Crear lotes nuevos (stock + fechas elaboración/vencimiento)\n" +
      "  · Actualizar lotes existentes con la cantidad disponible del Excel\n\n" +
      "Sólo procesa lotes con stock > 0 y no vencidos.\n\n¿Continuar?"
    )) return;
    setLoading(true);
    setResult(null);
    setMode(apply ? "apply" : "dry-run");
    try {
      const { data } = await api.post(`/admin/migration/inventario-2026?apply=${apply}`);
      setResult(data);
      if (data.ok) toast.success(apply ? "Inventario actualizado" : "Dry-run completado");
      else toast.error(`Error: ${data.error}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 mt-6 border-2 border-[#8a5500]/40 bg-gradient-to-br from-[#fef7ec] to-white" data-testid="inventario-update-card">
      <h2 className="text-xl font-bold text-[#15241d] mb-2 flex items-center gap-2">
        <Database size={20} className="text-[#8a5500]" />
        Actualizar Inventario · 2026 v2
      </h2>
      <p className="text-sm text-[#4a5c53] mb-4">
        Actualiza precios y crea/actualiza lotes de productos desde los archivos:
        <code className="text-xs ml-1">stock_2026.xlsx</code> y <code className="text-xs">gestion_2026.xlsx</code>.
        <br />Sólo procesa lotes con <strong>stock &gt; 0</strong> y <strong>no vencidos</strong>.
        Toma cantidad del archivo stock; enriquece fecha elaboración + registro sanitario del archivo gestion.
        <br /><span className="text-[#5c7368] italic">Idempotente — re-correr actualiza sin duplicar.</span>
      </p>
      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => run(false)} disabled={loading} variant="outline"
          className="border-[#8a5500] text-[#8a5500]" data-testid="inventario-update-dryrun-btn">
          {loading && mode === "dry-run" ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
          Dry-run (sólo simular)
        </Button>
        <Button onClick={() => run(true)} disabled={loading} className="bg-[#8a5500] hover:bg-[#6e4400]" data-testid="inventario-update-apply-btn">
          {loading && mode === "apply" ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle2 className="mr-2" size={16} />}
          Aplicar actualización
        </Button>
      </div>

      {result && (
        <div className="mt-4 rounded-md border border-[#d1dbd6] bg-white p-3" data-testid="inventario-update-result">
          {result.ok ? (
            <pre className="text-xs whitespace-pre-wrap font-mono leading-snug max-h-[60vh] overflow-y-auto">
              {result.stdout}
            </pre>
          ) : (
            <div className="text-sm text-[#7a2514]">
              <p className="font-semibold">Error: {result.error}</p>
              <pre className="text-xs mt-2 whitespace-pre-wrap">{result.traceback}</pre>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


function CarteraReconcileCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("dry-run");

  const run = async (apply) => {
    if (apply && !window.confirm(
      "⚠️ Reconciliar CxC con archivo CARTERA 2026 v2.\n\n" +
      "Esto va a:\n" +
      "  · Marcar como 'pagada' las CxC pendientes que NO están en la cartera\n" +
      "  · Re-marcar pendientes las que estaban pagadas pero SÍ están en cartera\n" +
      "  · Actualizar saldos cuando difieren entre BD y cartera\n" +
      "  · Crear nuevas CxC para facturas en cartera que no existen en BD\n\n" +
      "La cartera es la fuente de verdad de qué está pendiente HOY.\n\n" +
      "¿Continuar?"
    )) return;
    setLoading(true);
    setResult(null);
    setMode(apply ? "apply" : "dry-run");
    try {
      const { data } = await api.post(`/admin/migration/cartera-2026?apply=${apply}`);
      setResult(data);
      if (data.ok) {
        toast.success(apply ? "Reconciliación aplicada" : "Dry-run completado");
      } else {
        toast.error(`Error: ${data.error}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 mt-6 border-2 border-[#0b66a1]/40 bg-gradient-to-br from-[#eaf3fb] to-white" data-testid="cartera-reconcile-card">
      <h2 className="text-xl font-bold text-[#15241d] mb-2 flex items-center gap-2">
        <Database size={20} className="text-[#0b66a1]" />
        Reconciliación · CARTERA 2026
      </h2>
      <p className="text-sm text-[#4a5c53] mb-4">
        Reconcilia las cuentas por cobrar contra el archivo <strong>CARTERA 2026 v2</strong>.
        La cartera es la fuente de verdad de qué está pendiente <strong>HOY</strong>:
        marcas como pagadas las que ya no están, re-abre las que están pero estaban pagadas,
        actualiza saldos y crea nuevas faltantes.
        <br /><span className="text-[#5c7368] italic">Idempotente — re-correr no duplica.</span>
      </p>
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={() => run(false)}
          disabled={loading}
          variant="outline"
          className="border-[#0b66a1] text-[#0b66a1]"
          data-testid="cartera-reconcile-dryrun-btn"
        >
          {loading && mode === "dry-run" ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
          Dry-run (sólo simular)
        </Button>
        <Button
          onClick={() => run(true)}
          disabled={loading}
          className="bg-[#0b66a1] hover:bg-[#094f7c]"
          data-testid="cartera-reconcile-apply-btn"
        >
          {loading && mode === "apply" ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle2 className="mr-2" size={16} />}
          Aplicar reconciliación
        </Button>
      </div>

      {result && (
        <div className="mt-4 rounded-md border border-[#d1dbd6] bg-white p-3" data-testid="cartera-reconcile-result">
          {result.ok ? (
            <pre className="text-xs whitespace-pre-wrap font-mono leading-snug max-h-[60vh] overflow-y-auto">
              {result.stdout}
            </pre>
          ) : (
            <div className="text-sm text-[#7a2514]">
              <p className="font-semibold">Error: {result.error}</p>
              <pre className="text-xs mt-2 whitespace-pre-wrap">{result.traceback}</pre>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


function Sync2026V2Card() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("dry-run");

  const run = async (apply) => {
    if (apply && !window.confirm(
      "⚠️ Aplicar SYNC del Excel COBROS-PAGOS 2026 v2 a la BD actual.\n\n" +
      "Esto va a:\n" +
      "  · Actualizar facturas existentes (saldo, estado: pendiente/pagada)\n" +
      "  · Insertar facturas nuevas que no estén en la BD\n" +
      "  · Marcar como 'pagada' las que estén 'C' en el Excel\n\n" +
      "El proceso es idempotente — se puede re-ejecutar sin duplicar.\n\n" +
      "¿Continuar?"
    )) return;
    setLoading(true);
    setResult(null);
    setMode(apply ? "apply" : "dry-run");
    try {
      const { data } = await api.post(`/admin/migration/cobros-pagos-2026-v2?apply=${apply}`);
      setResult(data);
      if (data.ok) {
        toast.success(apply ? "Sync 2026 v2 aplicado" : "Dry-run completado · revisa el resumen");
      } else {
        toast.error(`Error: ${data.error}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 mt-6 border-2 border-[#205c45]/40 bg-gradient-to-br from-[#e9f2ee] to-white" data-testid="sync-2026-v2-card">
      <h2 className="text-xl font-bold text-[#15241d] mb-2 flex items-center gap-2">
        <Database size={20} className="text-[#205c45]" />
        Sync · COBROS-PAGOS 2026 v2
      </h2>
      <p className="text-sm text-[#4a5c53] mb-4">
        Sincroniza el Excel <strong>COBROS-PAGOS 2026 v2</strong> contra esta BD.
        Para cada fila: actualiza si ya existe (saldo/estado), inserta si es nueva,
        marca como <strong>"pagada"</strong> las que tengan Estado "C" en el Excel.
        <br /><span className="text-[#5c7368] italic">Idempotente — puedes re-ejecutar sin duplicar.</span>
      </p>
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={() => run(false)}
          disabled={loading}
          variant="outline"
          className="border-[#205c45] text-[#205c45]"
          data-testid="sync-2026-v2-dryrun-btn"
        >
          {loading && mode === "dry-run" ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
          Dry-run (sólo simular)
        </Button>
        <Button
          onClick={() => run(true)}
          disabled={loading}
          className="bg-[#205c45] hover:bg-[#1a4a37]"
          data-testid="sync-2026-v2-apply-btn"
        >
          {loading && mode === "apply" ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle2 className="mr-2" size={16} />}
          Aplicar sync
        </Button>
      </div>

      {result && (
        <div className="mt-4 rounded-md border border-[#d1dbd6] bg-white p-3" data-testid="sync-2026-v2-result">
          {result.ok ? (
            <pre className="text-xs whitespace-pre-wrap font-mono leading-snug max-h-[60vh] overflow-y-auto">
              {result.stdout}
            </pre>
          ) : (
            <div className="text-sm text-[#7a2514]">
              <p className="font-semibold">Error: {result.error}</p>
              <pre className="text-xs mt-2 whitespace-pre-wrap">{result.traceback}</pre>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


function DiagnosticoCard() {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/db-diagnostico");
      setDiag(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const ejecutarBackfill = async () => {
    if (!window.confirm("Recalcular total = saldo + monto_pagado en CxC/CxP. ¿Continuar?")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/backfill-totales");
      toast.success(`Backfill aplicado · ${data.fixed.cxc} CxC + ${data.fixed.cxp} CxP corregidos`);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBackfilling(false); }
  };

  const compararKPI = (real, esperado, label) => {
    const diff = Math.abs(real - esperado);
    const ok = diff < 1; // tolerancia 1 dolar
    return (
      <div className={`flex justify-between text-xs py-1 ${ok ? "" : "text-[#a3322a]"}`}>
        <span className="font-mono">{label}</span>
        <span className="tabular-nums">
          {real?.toLocaleString("es-EC")} {ok ? "✓" : `(esperado ${esperado.toLocaleString("es-EC")})`}
        </span>
      </div>
    );
  };

  return (
    <Card className="p-6 mt-6 border-2 border-[#345b80]/30 bg-gradient-to-br from-[#eaf1f7] to-white">
      <h2 className="text-xl font-bold text-[#15241d] mb-2 flex items-center gap-2">
        <Database size={22} className="text-[#345b80]" /> Diagnóstico de la base de datos
      </h2>
      <p className="text-sm text-[#5c7368] mb-4">
        Verifica que los cambios de Iter59 (gastos operativos, NCC, SOLCA Manabí, COLORADO BAHIA, backfill totales) estén aplicados en este entorno.
      </p>
      <Button onClick={cargar} disabled={loading} variant="outline" data-testid="diag-load-btn">
        {loading ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
        Generar diagnóstico
      </Button>

      {diag && (
        <div className="mt-4 space-y-4">
          <div className="bg-white rounded-lg p-4 border">
            <h3 className="font-semibold text-sm mb-2">Conteos por colección</h3>
            <div className="grid grid-cols-2 gap-x-4 text-xs">
              {Object.entries(diag.counts || {}).map(([col, n]) => (
                <div key={col} className="flex justify-between py-0.5 border-b">
                  <span className="font-mono">{col}</span>
                  <span className="tabular-nums font-semibold">{n.toLocaleString("es-EC")}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border">
            <h3 className="font-semibold text-sm mb-2">Iter59 — datos importados</h3>
            <div className="text-xs space-y-1">
              <div className="flex justify-between">
                <span>Gastos operativos con origen iter59</span>
                <span className="tabular-nums">
                  {diag.iter59_gastos.total_con_origen_iter59} / esperado 331 {diag.iter59_gastos.total_con_origen_iter59 >= 331 ? "✓" : "✗"}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total monto Iter59</span>
                <span className="tabular-nums">${diag.iter59_gastos.total_anual_2026?.toLocaleString("es-EC")}</span>
              </div>
              <div className="flex justify-between">
                <span>NCC con origen iter59</span>
                <span className="tabular-nums">{diag.iter59_ncc.total_con_origen_iter59} / esperado 13</span>
              </div>
              <div className="flex justify-between">
                <span>Proveedor COLORADO BAHIA GARZOTA</span>
                <span>{typeof diag.iter59_proveedor_colorado === "object" ? "✓ EXISTE" : "✗ FALTA"}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border">
            <h3 className="font-semibold text-sm mb-2">SOLCA Manabí (validación)</h3>
            {typeof diag.solca_manabi === "object" ? (
              <div className="text-xs space-y-1">
                <div className="flex justify-between"><span>Razón social</span><span>{diag.solca_manabi.razon_social}</span></div>
                <div className="flex justify-between"><span>Email</span><span>{diag.solca_manabi.email || "(sin email)"}</span></div>
                <div className="flex justify-between"><span>N° CxC</span><span className="tabular-nums">{diag.solca_manabi.n_cxc}</span></div>
                <div className="flex justify-between font-semibold"><span>Saldo pendiente</span><span className="tabular-nums">${diag.solca_manabi.saldo_pendiente?.toLocaleString("es-EC")}</span></div>
              </div>
            ) : <span className="text-[#a3322a]">SOLCA Manabí no existe</span>}
          </div>

          <div className="bg-white rounded-lg p-4 border">
            <h3 className="font-semibold text-sm mb-2">KPIs financieros vs esperado preview</h3>
            <div className="text-xs space-y-1">
              {compararKPI(diag.kpis.cxc_saldo_pendiente, diag.valores_esperados_preview.cxc_saldo_pendiente, "CxC saldo pendiente")}
              {compararKPI(diag.kpis.cxp_saldo_pendiente, diag.valores_esperados_preview.cxp_saldo_pendiente, "CxP saldo pendiente")}
              {compararKPI(diag.solca_manabi?.saldo_pendiente || 0, diag.valores_esperados_preview.solca_manabi_saldo, "SOLCA Manabí saldo")}
            </div>
          </div>

          {diag.backfill_pendiente?.needs_backfill && (
            <div className="bg-[#fef7ec] rounded-lg p-4 border-2 border-[#c8943f]">
              <h3 className="font-semibold text-sm mb-2 text-[#8a5500]">⚠ Backfill pendiente</h3>
              <p className="text-xs mb-2">
                Hay {diag.backfill_pendiente.cxc_con_total_invalid} CxC y {diag.backfill_pendiente.cxp_con_total_invalid} CxP con total=null/0. Esto causa que el dashboard muestre saldos incorrectos.
              </p>
              <Button onClick={ejecutarBackfill} disabled={backfilling} className="bg-[#c8943f] hover:bg-[#8a5500] text-white" data-testid="backfill-btn">
                {backfilling ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
                Ejecutar backfill (recalcular totales)
              </Button>
            </div>
          )}

          {(diag.iter59_gastos.total_con_origen_iter59 < 331 || diag.iter59_ncc.total_con_origen_iter59 < 13 || typeof diag.iter59_proveedor_colorado !== "object") && (
            <Iter59ApplyCard onComplete={cargar} />
          )}
        </div>
      )}
    </Card>
  );
}


function Iter59ApplyCard({ onComplete }) {
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const aplicar = async () => {
    if (confirmText.trim().toUpperCase() !== "APLICAR") {
      toast.error("Escribe APLICAR para confirmar");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/admin/aplicar-iter59-only?confirm=APLICAR_ITER59");
      setResult(data);
      toast.success("Iter59 aplicado al entorno");
      if (onComplete) await onComplete();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); setConfirmText(""); }
  };

  return (
    <div className="bg-[#eaf1f7] rounded-lg p-4 border-2 border-[#345b80]" data-testid="iter59-apply-card">
      <h3 className="font-semibold text-sm mb-2 text-[#345b80]">⏬ Aplicar Iter59 (datos faltantes)</h3>
      <p className="text-xs mb-3">
        Insertará en la BD actual los datos del snapshot que el diagnóstico marca como faltantes:
      </p>
      <ul className="text-xs list-disc list-inside mb-3 space-y-0.5">
        <li>Proveedor <b>COLORADO BAHIA GARZOTA</b> (RUC 0993393444001)</li>
        <li><b>331 gastos operativos</b> históricos 2026 ($58,266 total)</li>
        <li><b>13 NCC</b> con origen Iter59</li>
      </ul>
      <p className="text-xs text-[#5c7368] mb-3">
        ✓ Idempotente: no duplica, no toca tus datos manuales. Re-ejecutable.
      </p>
      <input
        type="text"
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        placeholder="APLICAR"
        className="border border-[#345b80]/30 rounded-md px-3 py-2 text-sm w-48 mr-2"
        data-testid="iter59-confirm-input"
      />
      <Button
        onClick={aplicar}
        disabled={applying || confirmText.trim().toUpperCase() !== "APLICAR"}
        className="bg-[#345b80] hover:bg-[#2a4a6a] text-white"
        data-testid="iter59-apply-btn"
      >
        {applying ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
        Aplicar Iter59
      </Button>
      {result && (
        <div className="mt-3 bg-white rounded p-3 text-xs">
          <div className="font-semibold mb-1">✓ Resultado:</div>
          {Object.entries(result.inserted || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between"><span>{k}</span><span className="tabular-nums">{v}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}


function CleanupCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [result, setResult] = useState(null);

  const cargarPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cleanup-preview");
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const ejecutar = async () => {
    if (confirmText.trim().toUpperCase() !== "BORRAR") {
      toast.error("Escribe BORRAR para confirmar");
      return;
    }
    setExecuting(true);
    try {
      const { data } = await api.post(
        "/admin/cleanup-execute?confirm=BORRAR_TEST_DATA"
      );
      setResult(data);
      toast.success(`Limpieza aplicada · ${data.total_deleted} docs eliminados`);
      await cargarPreview();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExecuting(false);
      setConfirmText("");
    }
  };

  return (
    <Card className="p-6 mt-6 border-2 border-[#c8943f]/40 bg-gradient-to-br from-[#fef7ec] to-white">
      <h2 className="text-xl font-bold text-[#15241d] mb-2 flex items-center gap-2">
        <ShieldAlert size={22} className="text-[#c8943f]" /> Limpieza de datos test (Iter60)
      </h2>
      <p className="text-sm text-[#5c7368] mb-4">
        Elimina datos basura: facturas/cotizaciones/NP de prueba, catálogos TEST_, asientos huérfanos, CxC con todos los montos en cero.
      </p>

      <Button onClick={cargarPreview} disabled={loading} variant="outline" data-testid="cleanup-preview-btn">
        {loading ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
        Ver qué se borraría (DRY-RUN)
      </Button>

      {preview && (
        <div className="mt-4 bg-white rounded-lg p-4 border">
          <div className="text-sm font-semibold mb-2">
            Total a eliminar: <span className="text-[#a3322a] text-lg">{preview.total_deleted}</span> documentos
          </div>
          <div className="space-y-1 text-xs max-h-64 overflow-auto">
            {Object.entries(preview.summary || {}).filter(([_, n]) => n > 0).map(([k, n]) => (
              <div key={k} className="flex justify-between py-0.5 border-b last:border-b-0">
                <span className="font-mono">{k}</span>
                <span className="tabular-nums font-semibold text-[#a3322a]">{n}</span>
              </div>
            ))}
          </div>

          {preview.total_deleted > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-[#a3322a] font-semibold mb-2">
                ⚠ ACCIÓN IRREVERSIBLE. Escribe BORRAR para habilitar el botón.
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="BORRAR"
                className="border border-[#a3322a]/30 rounded-md px-3 py-2 text-sm w-48 mr-2"
                data-testid="cleanup-confirm-input"
              />
              <Button
                onClick={ejecutar}
                disabled={executing || confirmText.trim().toUpperCase() !== "BORRAR"}
                className="bg-[#a3322a] hover:bg-[#8a2820] text-white"
                data-testid="cleanup-execute-btn"
              >
                {executing ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
                Aplicar limpieza
              </Button>
            </div>
          )}

          {preview.total_deleted === 0 && (
            <p className="text-sm text-[#205c45] font-semibold mt-3">✓ Nada que limpiar — la BD ya está limpia</p>
          )}
        </div>
      )}

      {result && !result.dry_run && (
        <div className="mt-4 bg-[#e9f2ee] rounded-lg p-4 border border-[#205c45]">
          <p className="text-sm font-bold text-[#205c45] mb-2">
            ✅ Limpieza aplicada · {result.total_deleted} documentos eliminados
          </p>
          <div className="text-xs space-y-1">
            {Object.entries(result.summary || {}).filter(([_, n]) => n > 0).map(([k, n]) => (
              <div key={k} className="flex justify-between">
                <span className="font-mono">{k}</span>
                <span className="tabular-nums">{n}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}



function ReconciliarCajasCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("dry-run");
  const [facturaId, setFacturaId] = useState("");
  const [facturaNumero, setFacturaNumero] = useState("");
  const [forzar, setForzar] = useState(false);

  const run = async (apply) => {
    if (apply && !window.confirm(
      "⚠️ Aplicar ajuste de stock para facturas vendidas POR CAJA pero descontadas como UNIDAD.\n\n" +
      "Por cada factura encontrada se descontará la diferencia faltante del lote " +
      "y se registrará un movimiento de stock tipo 'ajuste_reconciliacion'.\n\n" +
      "Idempotente. ¿Continuar?"
    )) return;
    setLoading(true);
    setResult(null);
    setMode(apply ? "apply" : "dry-run");
    try {
      const params = new URLSearchParams({ aplicar: apply ? "true" : "false" });
      if (facturaId) params.set("factura_id", facturaId);
      if (forzar) params.set("forzar", "true");
      const { data } = await api.post(`/invoicing/facturas/reconciliar-cajas-descontadas-como-unidad?${params.toString()}`);
      setResult(data);
      toast.success(apply
        ? `${data.con_ajuste_pendiente} factura(s) ajustadas`
        : `Vista previa · ${data.con_ajuste_pendiente} factura(s) requieren ajuste`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const buscarFacturaPorNumero = async () => {
    if (!facturaNumero.trim()) return;
    try {
      const { data } = await api.get(`/invoicing/facturas?search=${encodeURIComponent(facturaNumero.trim())}`);
      const found = (data || []).find(f => (f.numero || "").includes(facturaNumero.trim()));
      if (found) {
        setFacturaId(found.id);
        toast.success(`Factura ${found.numero} encontrada · ${found.cliente_nombre}`);
      } else {
        toast.error("No se encontró factura con ese número");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="p-6 border border-[#d1dbd6]" data-testid="reconciliar-cajas-card">
      <h2 className="text-lg font-semibold text-[#15241d] mb-1 flex items-center gap-2">
        <RefreshCw size={18} className="text-[#b08400]" />
        Reconciliar stock: cajas descontadas como unidad
      </h2>
      <p className="text-sm text-[#5c7368] mb-4">
        Detecta facturas autorizadas vendidas POR CAJA pero cuyo descuento de stock se registró como UNIDAD
        (1 unidad en lugar de N tabletas por caja). Bug presente antes del fix ITER96 — solo afecta facturas
        editadas en su flujo de edición. Aplica el delta faltante al lote original.
      </p>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5c7368]">N° de factura (opcional)</label>
          <div className="flex gap-2 mt-1">
            <input
              type="text"
              value={facturaNumero}
              onChange={(e) => setFacturaNumero(e.target.value)}
              placeholder="Ej: 001-002-000005748"
              className="flex-1 h-9 px-2 border border-[#d1dbd6] rounded text-sm font-mono"
              data-testid="reconciliar-cajas-factura-input"
            />
            <Button variant="outline" size="sm" onClick={buscarFacturaPorNumero}>Buscar</Button>
          </div>
          {facturaId && (
            <div className="mt-1 text-xs text-[#205c45] flex items-center gap-2">
              <CheckCircle2 size={12} /> ID: <code className="text-[10px]">{facturaId.slice(0, 8)}…</code>
              <button onClick={() => { setFacturaId(""); setFacturaNumero(""); }} className="text-[#b53a25] underline">limpiar</button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={() => run(false)} disabled={loading} variant="outline" data-testid="reconciliar-cajas-preview-btn">
          {loading && mode === "dry-run" ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
          Vista previa
        </Button>
        <Button
          onClick={() => run(true)}
          disabled={loading || !result || result.con_ajuste_pendiente === 0}
          className="bg-[#b08400] hover:bg-[#8f6c00]"
          data-testid="reconciliar-cajas-apply-btn"
        >
          {loading && mode === "apply" ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
          Aplicar
        </Button>
        <label className="ml-3 flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={forzar}
            onChange={(e) => setForzar(e.target.checked)}
            data-testid="reconciliar-cajas-forzar"
          />
          <span className={forzar ? "text-[#b53a25] font-semibold" : "text-[#5c7368]"}>
            Forzar (usar lote alternativo y/o permitir saldo negativo)
          </span>
        </label>
      </div>

      {result && (
        <div className="mt-4 p-3 bg-[#f7f9f8] rounded border border-[#d1dbd6] text-sm">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={result.aplicar ? "bg-[#205c45]" : "bg-[#5c7368]"}>
              {result.aplicar ? "APLICADO" : "DRY-RUN"}
            </Badge>
            <span className="font-mono text-xs">
              procesadas={result.facturas_procesadas} ·
              ya_correctas={result.ya_correctas} ·
              ajustes_pendientes={result.con_ajuste_pendiente} ·
              problemas={result.con_problema}
            </span>
          </div>
          {result.detalles?.length > 0 && (
            <div className="overflow-auto max-h-72 mt-2 text-xs">
              <table className="w-full">
                <thead className="bg-[#e9f2ee] text-[#15241d]">
                  <tr>
                    <th className="text-left p-1">Factura</th>
                    <th className="text-left p-1">Cliente</th>
                    <th className="text-left p-1">Producto</th>
                    <th className="text-left p-1">Lote</th>
                    <th className="text-right p-1">Esperado</th>
                    <th className="text-right p-1">Descontado</th>
                    <th className="text-right p-1">Ajuste</th>
                    <th className="p-1">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {result.detalles.flatMap((d, i) => {
                    if (d.estado === "error") {
                      return [(
                        <tr key={`${i}-err`} className="border-b border-[#d1dbd6] bg-[#fdecea]">
                          <td className="font-mono p-1">{d.factura}</td>
                          <td className="p-1 italic text-[#b53a25]" colSpan={6}>
                            ⚠ {d.problema || "Error desconocido"}
                          </td>
                          <td className="p-1 uppercase text-[10px] text-[#b53a25]">ERROR</td>
                        </tr>
                      )];
                    }
                    return (d.ajustes || []).map((a, j) => (
                      <tr key={`${i}-${j}`} className="border-b border-[#d1dbd6]">
                        <td className="font-mono p-1">{d.factura}</td>
                        <td className="p-1">{d.cliente}</td>
                        <td className="p-1">{a.producto}</td>
                        <td className="font-mono p-1">{a.lote}</td>
                        <td className="text-right tabular-nums p-1">{a.esperado}</td>
                        <td className="text-right tabular-nums text-[#b53a25] p-1">{a.descontado_actual}</td>
                        <td className="text-right tabular-nums text-[#205c45] font-semibold p-1">−{a.ajuste_negativo}</td>
                        <td className="p-1 uppercase text-[10px]">{d.estado}</td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


function RecalcularNombresCard() {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);

  const cargarPreview = async () => {
    setLoading(true);
    setPreview(null);
    setApplied(null);
    try {
      const { data } = await api.post("/masters/productos-comerciales/recalcular-nombres?dry_run=true");
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const aplicar = async () => {
    if (!preview || preview.total_a_cambiar === 0) return;
    if (!window.confirm(
      `¿Aplicar el renombrado a ${preview.total_a_cambiar} productos comerciales?\n\n` +
      `Formato: PRINCIPIO_ACTIVO + FORMA_FARMACEUTICA + CONCENTRACION + (MARCA)\n\n` +
      `Esta acción es reversible solo manualmente (queda registrado en _nombre_anterior).`
    )) return;
    setApplying(true);
    try {
      const { data } = await api.post("/masters/productos-comerciales/recalcular-nombres?dry_run=false");
      setApplied(data);
      setPreview(null);
      toast.success(`✓ ${data.aplicados} productos renombrados`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card className="p-6 mt-6 border-2 border-[#7b3ea8]/40 bg-gradient-to-br from-[#f4ecfa] to-white" data-testid="recalcular-nombres-card">
      <h2 className="text-xl font-bold text-[#15241d] mb-2 flex items-center gap-2">
        <RefreshCw size={20} className="text-[#7b3ea8]" />
        Recalcular nombres comerciales
      </h2>
      <p className="text-sm text-[#4a5c53] mb-3">
        Renombra los productos comerciales con el formato estructurado:&nbsp;
        <code className="bg-white px-1 rounded">PRINCIPIO_ACTIVO + FORMA_FARMACEUTICA + CONCENTRACION + (MARCA)</code>.
        Toma los campos del producto base relacionado.
      </p>
      <div className="bg-white rounded border border-[#d1c5e0] p-3 mb-4 text-xs">
        <div className="font-semibold text-[#5c7368] mb-1">Ejemplos:</div>
        <div className="font-mono text-[11px] text-[#205c45]">
          • ACETATO DE ABIRATERONA SOLIDO ORAL 250 MG (MATIZ)<br />
          • PARACETAMOL TABLETA 500 MG <span className="text-[#8a8a8a]">(sin marca)</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button
          onClick={cargarPreview}
          disabled={loading || applying}
          variant="outline"
          className="border-[#7b3ea8] text-[#7b3ea8] hover:bg-[#f4ecfa]"
          data-testid="recalcular-nombres-preview-btn"
        >
          {loading ? <Loader2 className="animate-spin mr-2" size={14} /> : <RefreshCw className="mr-2" size={14} />}
          1. Ver Preview (dry-run)
        </Button>
        {preview && preview.total_a_cambiar > 0 && (
          <Button
            onClick={aplicar}
            disabled={applying}
            className="bg-[#7b3ea8] hover:bg-[#5d2b85] text-white"
            data-testid="recalcular-nombres-apply-btn"
          >
            {applying ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
            2. Aplicar a {preview.total_a_cambiar} productos
          </Button>
        )}
      </div>

      {preview && (
        <div className="bg-white rounded border border-[#d1c5e0] p-3">
          <div className="flex flex-wrap gap-3 text-xs mb-3">
            <Badge variant="outline" className="border-[#7b3ea8] text-[#7b3ea8]">A cambiar: {preview.total_a_cambiar}</Badge>
            <Badge variant="outline" className="border-[#5c7368] text-[#5c7368]">Sin cambios: {preview.sin_cambios}</Badge>
            {preview.sin_producto_base > 0 && (
              <Badge variant="outline" className="border-[#c8943f] text-[#c8943f]">Sin producto base: {preview.sin_producto_base}</Badge>
            )}
          </div>
          {preview.muestra && preview.muestra.length > 0 && (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#f4ecfa] z-10">
                  <tr>
                    <th className="text-left p-2 font-semibold text-[#5c2b85]">Código</th>
                    <th className="text-left p-2 font-semibold text-[#5c2b85]">Antes</th>
                    <th className="text-left p-2 font-semibold text-[#5c2b85]">Después</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.muestra.map((c) => (
                    <tr key={c.id} className="border-b border-[#eee] hover:bg-[#fafafa]">
                      <td className="p-2 font-mono text-[10px] whitespace-nowrap">{c.codigo_interno}</td>
                      <td className="p-2 text-[#8a8a8a] line-through">{c.nombre_anterior || <em>(vacío)</em>}</td>
                      <td className="p-2 text-[#205c45] font-semibold">{c.nombre_nuevo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.total_a_cambiar > preview.muestra.length && (
                <div className="text-[11px] text-[#5c7368] mt-2 text-center">
                  Mostrando primeros {preview.muestra.length} de {preview.total_a_cambiar} cambios.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {applied && (
        <div className="bg-[#e9f2ee] border border-[#205c45] rounded p-3 mt-3 text-sm">
          <div className="flex items-center gap-2 text-[#205c45] font-semibold">
            <CheckCircle2 size={16} />
            Renombrado completado
          </div>
          <div className="text-xs text-[#15241d] mt-1">
            {applied.aplicados} productos comerciales actualizados. El nombre anterior queda guardado en{" "}
            <code>_nombre_anterior</code> de cada registro.
          </div>
        </div>
      )}
    </Card>
  );
}


function SyncPrecioCajaCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const ejecutar = async () => {
    if (!window.confirm(
      "🔄 Sincronizar precio_caja_venta = precio_venta × unidades_por_presentacion\n\n" +
      "Esto corrige el bug donde el precio de CAJA aparece distinto al precio de UNIDAD × cantidad por caja " +
      "(ej. unidad=$3.90 × 10 jeringas debería ser $39.00 pero aparecía $43.33).\n\n" +
      "Idempotente. ¿Continuar?"
    )) return;
    setLoading(true);
    try {
      const { data } = await api.post("/masters/productos-comerciales/sincronizar-precio-caja");
      setResult(data);
      toast.success(`✓ ${data.productos_actualizados} productos sincronizados (de ${data.productos_revisados})`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 mt-6 border-2 border-[#205c45]/40 bg-gradient-to-br from-[#e9f2ee] to-white" data-testid="sync-precio-caja-card">
      <h2 className="text-xl font-bold text-[#15241d] mb-2 flex items-center gap-2">
        <RefreshCw size={20} className="text-[#205c45]" />
        Sincronizar precio de caja con precio de unidad · Feb 2026
      </h2>
      <p className="text-sm text-[#4a5c53] mb-3">
        Recalcula <code className="bg-white px-1 rounded">precio_caja_venta = precio_venta × unidades_por_presentacion</code>{" "}
        para todos los productos. Corrige el caso donde la caja muestra un precio distinto al esperado
        (ej. ENOXAPARINA: unidad $3.90 × 10 jeringas = <strong>$39.00</strong>, pero la caja aparecía
        en $43.33 por un cálculo viejo de costo+margen).
      </p>

      <Button
        onClick={ejecutar}
        disabled={loading}
        className="bg-[#205c45] hover:bg-[#15241d] text-white"
        data-testid="sync-precio-caja-btn"
      >
        {loading ? <Loader2 className="animate-spin mr-2" size={14} /> : <RefreshCw className="mr-2" size={14} />}
        Ejecutar sincronización
      </Button>

      {result && (
        <div className="bg-white rounded border border-[#b2d0c2] p-3 mt-4">
          <div className="flex flex-wrap gap-3 text-xs mb-3">
            <Badge variant="outline" className="border-[#205c45] text-[#205c45]">Revisados: {result.productos_revisados}</Badge>
            <Badge variant="outline" className="border-[#205c45] text-[#205c45] font-bold">Actualizados: {result.productos_actualizados}</Badge>
            <Badge variant="outline" className="border-[#5c7368] text-[#5c7368]">Sin cambios: {result.sin_cambio}</Badge>
          </div>
          {result.muestra && result.muestra.length > 0 && (
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#e9f2ee] z-10">
                  <tr>
                    <th className="text-left p-2 font-semibold text-[#205c45]">Código</th>
                    <th className="text-left p-2 font-semibold text-[#205c45]">Producto</th>
                    <th className="text-right p-2 font-semibold text-[#205c45]">Unidad</th>
                    <th className="text-right p-2 font-semibold text-[#205c45]">Upc</th>
                    <th className="text-right p-2 font-semibold text-[#205c45]">Caja antes</th>
                    <th className="text-right p-2 font-semibold text-[#205c45]">Caja nuevo</th>
                  </tr>
                </thead>
                <tbody>
                  {result.muestra.map((m, idx) => (
                    <tr key={idx} className="border-b border-[#eee] hover:bg-[#fafafa]">
                      <td className="p-2 font-mono text-[10px] whitespace-nowrap">{m.codigo_interno}</td>
                      <td className="p-2 text-[#15241d]">{m.nombre}</td>
                      <td className="p-2 text-right tabular-nums">${(+m.precio_venta).toFixed(4)}</td>
                      <td className="p-2 text-right tabular-nums">{m.upc}</td>
                      <td className="p-2 text-right tabular-nums text-[#8a8a8a] line-through">
                        {m.precio_caja_anterior != null ? `$${(+m.precio_caja_anterior).toFixed(4)}` : "—"}
                      </td>
                      <td className="p-2 text-right tabular-nums text-[#205c45] font-bold">${(+m.precio_caja_nuevo).toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.productos_actualizados > result.muestra.length && (
                <div className="text-[11px] text-[#5c7368] mt-2 text-center">
                  Mostrando primeros {result.muestra.length} de {result.productos_actualizados} cambios.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function RestaurarCostosCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const ejecutar = async () => {
    if (!window.confirm(
      "💰 Restaurar costos en $0 desde histórico\n\n" +
      "Busca todos los productos con precio_costo = 0 y restaura el último costo conocido\n" +
      "desde el histórico de compras (precio_history). Si no hay histórico, usa el promedio\n" +
      "ponderado de los lotes activos con costo > 0.\n\n" +
      "Caso: VALPRAX 250mg — costo $0 (editado por error) se restaura a $2.01 (última compra).\n\n" +
      "Idempotente. ¿Continuar?"
    )) return;
    setLoading(true);
    try {
      const { data } = await api.post("/masters/productos-comerciales/restaurar-costos-desde-historico");
      setResult(data);
      if (data.productos_actualizados > 0) {
        toast.success(`✓ ${data.productos_actualizados} productos restaurados`);
      } else {
        toast.info(`No hay productos con costo=$0 que requieran restauración`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 mt-6 border-2 border-[#a55700]/40 bg-gradient-to-br from-[#fff3e0] to-white" data-testid="restaurar-costos-card">
      <h2 className="text-xl font-bold text-[#15241d] mb-2 flex items-center gap-2">
        <RefreshCw size={20} className="text-[#a55700]" />
        Restaurar costos en $0 desde histórico · Feb 2026
      </h2>
      <p className="text-sm text-[#4a5c53] mb-3">
        Recupera el <code className="bg-white px-1 rounded">precio_costo</code> de productos que
        quedaron en <strong>$0</strong> por una edición accidental. Toma el último valor desde el
        histórico de compras (<code className="bg-white px-1 rounded">precio_history</code>) o el
        promedio ponderado de lotes activos. Propaga el costo restaurado a los lotes que tengan
        costo = 0.
      </p>
      <div className="bg-[#fff8ef] border border-[#a55700]/30 rounded p-2 text-[11px] text-[#7a4a00] mb-3">
        <strong>Bonus:</strong> A partir de este deploy, el sistema <em>ya no permite</em> que una
        edición manual baje el costo a $0 cuando el producto tiene stock o histórico de compras.
        El costo previo se conserva automáticamente.
      </div>

      <Button
        onClick={ejecutar}
        disabled={loading}
        className="bg-[#a55700] hover:bg-[#7a4000] text-white"
        data-testid="restaurar-costos-btn"
      >
        {loading ? <Loader2 className="animate-spin mr-2" size={14} /> : <RefreshCw className="mr-2" size={14} />}
        Ejecutar restauración
      </Button>

      {result && (
        <div className="bg-white rounded border border-[#dfb88a] p-3 mt-4">
          <div className="flex flex-wrap gap-3 text-xs mb-3">
            <Badge variant="outline" className="border-[#a55700] text-[#a55700]">Revisados: {result.productos_revisados}</Badge>
            <Badge variant="outline" className="border-[#205c45] text-[#205c45] font-bold">Restaurados: {result.productos_actualizados}</Badge>
            <Badge variant="outline" className="border-[#5c7368] text-[#5c7368]">Sin datos para recuperar: {result.productos_sin_datos}</Badge>
          </div>
          {result.muestra && result.muestra.length > 0 && (
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#fff3e0] z-10">
                  <tr>
                    <th className="text-left p-2 font-semibold text-[#a55700]">Código</th>
                    <th className="text-left p-2 font-semibold text-[#a55700]">Producto</th>
                    <th className="text-right p-2 font-semibold text-[#a55700]">Costo antes</th>
                    <th className="text-right p-2 font-semibold text-[#a55700]">Costo restaurado</th>
                    <th className="text-left p-2 font-semibold text-[#a55700]">Fuente</th>
                  </tr>
                </thead>
                <tbody>
                  {result.muestra.map((m, idx) => (
                    <tr key={idx} className="border-b border-[#eee] hover:bg-[#fafafa]">
                      <td className="p-2 font-mono text-[10px] whitespace-nowrap">{m.codigo_interno}</td>
                      <td className="p-2 text-[#15241d]">{m.nombre}</td>
                      <td className="p-2 text-right tabular-nums text-[#a55700] line-through">
                        {m.costo_anterior != null ? `$${(+m.costo_anterior).toFixed(4)}` : "—"}
                      </td>
                      <td className="p-2 text-right tabular-nums text-[#205c45] font-bold">${(+m.costo_restaurado).toFixed(4)}</td>
                      <td className="p-2 text-[10px] text-[#5c7368]">{m.fuente}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.productos_actualizados > result.muestra.length && (
                <div className="text-[11px] text-[#5c7368] mt-2 text-center">
                  Mostrando primeros {result.muestra.length} de {result.productos_actualizados} cambios.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

